import React, { useCallback, useEffect } from 'react';
import { MetadataCategory } from '@cube3/common/model/schema/resources/metadata-category';
import Button from '@cube3/ui/src/Buttons/Button';
import { ModalMenuUI } from '@cube3/ui/src/Modal/ModalMenuUI';
import { Typography } from '@cube3/ui/src/typography/Typography';
import { createStyles, makeStyles } from '@material-ui/core';
import { useGlobalSubmit } from '@cube3/cubicle/src/helpers/hooks/useGlobalSubmit';

interface Props {
  loading: boolean;
  retrievedForms?: MetadataCategory[];
  allFormsValid: boolean;
  allFormsSucceeded?: boolean;
  error?: string;
  hasRequiredFields?: boolean;
  hasPrefilledFields?: boolean;
  title: string;
  hasProjectNameTemplateFeature?: boolean;
  onClickCreate: (e: React.SyntheticEvent) => void;
  renderMetaDataForm: (form: MetadataCategory) => JSX.Element | React.ReactNode;
  onCloseEvent: () => void;
  onClickPrevious: () => void;
  onNext: () => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    root: {},
    text: {
      paddingBottom: 10
    }
  })
);

let firstMount = true;

export const EditNewProjectMetadataModal: React.FC<
  React.PropsWithChildren<Props>
> = (props) => {
  const {
    onCloseEvent,
    loading,
    retrievedForms,
    renderMetaDataForm,
    onClickCreate,
    allFormsValid,
    allFormsSucceeded,
    onNext,
    error,
    title,
    hasRequiredFields,
    hasPrefilledFields,
    hasProjectNameTemplateFeature,
    onClickPrevious
  } = props;
  const classes = useStyles();

  // after finishing create project, close modal and go to newly created project
  useEffect(() => {
    // if the current modal is opened from `AddProjectModal` by clicking `Previous`,
    // we don't want it to trigger the next step immediately (will open `AddProjectModal` again)
    if (allFormsSucceeded && firstMount) {
      onNext();
    }
  }, [allFormsSucceeded, firstMount]);

  useEffect(() => {
    return () => {
      firstMount = false;
    };
  }, []);

  const handleClickCreate = useCallback(
    (e) => {
      onClickCreate(e);
      firstMount = true;
    },
    [onClickCreate]
  );

  const invalid = !allFormsValid || !!error;
  useGlobalSubmit(onClickCreate, invalid || loading);

  return (
    <ModalMenuUI
      title={title}
      loading={loading && !error}
      wide={true}
      onCloseEvent={onCloseEvent}
      footerLeftComponent={
        hasProjectNameTemplateFeature ? null : (
          <Button
            text="Previous"
            colorVariant="ghost2"
            onClick={onClickPrevious}
          />
        )
      }
      footerRightComponent={
        <>
          <Button text="Cancel" colorVariant="ghost2" onClick={onCloseEvent} />
          <Button
            text={hasProjectNameTemplateFeature ? 'Next' : 'Create'}
            colorVariant="filled1"
            disabled={invalid}
            onClick={handleClickCreate}
          />
        </>
      }
    >
      {hasRequiredFields && (
        <Typography color="contrast2" variant="body2" className={classes.text}>
          {
            'This project type has "Project Metadata" fields that are required, '
          }
          please fill these out before proceeding.
        </Typography>
      )}

      {hasPrefilledFields && (
        <Typography color="contrast2" variant="body2" className={classes.text}>
          {
            " We've pre-filled some fields based on your selected filters in the"
          }
          project navigator.
        </Typography>
      )}

      <Typography color="danger1" variant="body2" className={classes.text}>
        {error}
      </Typography>

      <div className={classes.root}>
        {!retrievedForms ? (
          <Typography typographyStyle={'body2'} color="contrast2">
            No project metadata yet
          </Typography>
        ) : (
          retrievedForms.map((form) => renderMetaDataForm(form))
        )}
      </div>
      {props.children}
    </ModalMenuUI>
  );
};
